import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { ExportDialogComponent } from '@core/components/export-dialog/export-dialog.component';
import { ExportDialogComponentInjectedData, ExportDialogTypeEnum } from '@core/components/export-dialog/export-dialog.types';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ExportDialogImrComponent } from '../../core/components/export-dialog-imr/export-dialog-imr.component';
import { SidenavService } from '../../core/services/sidenav.service';
import { DialogCreateMaintenanceComponent } from './dialogs/dialog-create-maintenance/dialog-create-maintenances.component';
import { MaintenanceFilterState } from './filter/services/maintenance-filter-state.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { defaultColumns } from './maintenances-default-columns';

@Component({
  selector: 'maintenances',
  templateUrl: './maintenances.component.html',
  styleUrls: ['./maintenances.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaintenancesComponent implements OnInit, OnDestroy {
  public reloadMaintenances = new Subject<boolean>();
  public columns: any[] = defaultColumns;
  public displayedColumns = this.loadColumnsFromStorage();
  public filtered: Boolean;
  private totalMaintenances = 0;
  public hasEditMaintenancePermission = this.authenticationService.hasPermission('maintenances-edit');
  public hasOpenMaintenancePermission = this.authenticationService.hasPermission('maintenances-open') && this.authenticationService.hasAccountPreference({
    property: 'openMaintenance',
    subProperty: 'featureDisableOpenMaintenanceOnPanel',
    expectedValue: false,
    defaultIfUndefined: false
  });
  public hasCreateExportPermission = this.authenticationService.hasPermission('exports-create');

  constructor(
    private matDialog: MatDialog,
    public sidenavService: SidenavService,
    private authenticationService: AuthenticationService,
    private maintenanceFilterState: MaintenanceFilterState,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.filtered = !isEqual(this.maintenanceFilterState.query, this.maintenanceFilterState.defaultQuery);
  }

  async ngOnInit(): Promise<void> {
    this.columns = this.columns.filter((column) => {
      return column.flag == null || this.authenticationService.hasFlags(column.flag);
    });

    this.displayedColumns = this.displayedColumns.filter((column) => {
      return this.columns.some((col) => col.name === column && (col.flag == null || this.authenticationService.hasFlags(col.flag)));
    });

    this.maintenanceFilterState.onChangeFilter.pipe(
      tap(() => {
        this.filtered = !isEqual(this.maintenanceFilterState.query, this.maintenanceFilterState.defaultQuery);
      })
    ).subscribe();
  }

  openDialog(): void {
    const maintenancesDialog = this.matDialog.open(DialogCreateMaintenanceComponent, {
      panelClass: 'field-dialog-1200'
    });

    maintenancesDialog.afterClosed().subscribe((reload) => {
      if (reload) {
        this.reloadMaintenances.next(reload);
      }
    });
  }


  clearFilters(): void {
    this.maintenanceFilterState.query = {};
    this.reloadMaintenances.next(true);
    this.maintenanceFilterState.onClearFilter.next();
  }

  updateTotalMaintenances(totalMaintenances): void {
    this.totalMaintenances = totalMaintenances;
  }

  public openExportDialog(): void {
    this.matDialog.open(ExportDialogComponent, {
      panelClass: 'export-dialog',
      autoFocus: false,
      data: {
        type: ExportDialogTypeEnum.maintenances,
        limit: 50000,
        total: this.totalMaintenances,
        where: this.maintenanceFilterState.query
      } as ExportDialogComponentInjectedData
    });
  }

  public openExportDialogImr(): void {
    this.matDialog.open(ExportDialogImrComponent, {
      panelClass: 'export-dialog-imr',
      autoFocus: false,
    });
  }

  async setColumn(event, columnName): Promise<void> {
    event.stopPropagation();

    const displayedColumnsIndex = this.displayedColumns.indexOf(columnName);

    const isSelected = displayedColumnsIndex > -1;

    if (isSelected && this.displayedColumns.length > 2) {
      this.displayedColumns.splice(displayedColumnsIndex, 1);
    }

    if (!isSelected) {
      this.displayedColumns = this.columns
        .map((column) => column.name)
        .filter((column) => column === columnName || this.displayedColumns.includes(column));
    }

    localStorage.setItem('maintenances-displayed-columns', JSON.stringify(this.displayedColumns));
  }

  private parseJson(jsonString: string): string[] {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return null;
    }
  }

  private loadColumnsFromStorage(): string[] {
    const savedColumnsAsJson = localStorage.getItem('maintenances-displayed-columns');
    const savedColumns = this.parseJson(savedColumnsAsJson);

    if (Array.isArray(savedColumns)) {
      const defaultColumnsNames = this.columns.map((column) => column.name);
      const savedColumnsFiltered = savedColumns.filter((column) => defaultColumnsNames.includes(column));
      if (savedColumnsFiltered.length) {
        localStorage.setItem('maintenances-displayed-columns', JSON.stringify(savedColumnsFiltered));
        return savedColumnsFiltered;
      }
    }

    const defaultActiveColumnsNames = this.columns.filter((column) => column.active).map((column) => column.name);
    localStorage.setItem('maintenances-displayed-columns', JSON.stringify(defaultActiveColumnsNames));
    return defaultActiveColumnsNames;
  }

  ngOnDestroy(): void {
    this.clearFilters();
  }
}
